import { AssetRequestError } from '../api/getAssetById';
import { getStoredLoginIdentifier } from '../auth/sessionToken';
import { sanitizeProfilePatchBody } from './profilePrivateClientPatch';
import { isPhoneLiteRegisterSyntheticEmail } from './profileLitePlaceholders';
import type { PrivateClientBookingField } from './profileRegistrationComplete';

const PROFILE_COMPLETION_PATCH_KEYS: PrivateClientBookingField[] = [
  'email',
  'phone_number',
  'first_name',
  'last_name',
  'birth_date',
  'occupation',
];

function coercePatchId(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    const n = Number(value.trim());
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function pickGeoFields(payload: Record<string, unknown>): Record<string, unknown> {
  const patch: Record<string, unknown> = {};
  const countryId = coercePatchId(payload.country_id);
  if (countryId !== null) {
    patch.country_id = countryId;
  }
  const stateId = coercePatchId(payload.state_id);
  if (stateId !== null) {
    patch.state_id = stateId;
  }
  return patch;
}

export function omitCompletionGeoFromPatch(body: Record<string, unknown>): Record<string, unknown> {
  const { country_id: _countryId, state_id: _stateId, ...rest } = body;
  return rest;
}

/** PATCH /profile for a signed-in user must not re-send email (backend 409 duplicate). */
export const SIGNED_IN_PROFILE_COMPLETION_OMIT_KEYS = ['email'] as const;

/** Phone OTP / lite accounts need a real email on the server; email-login accounts must not PATCH email. */
export function shouldOmitEmailFromSignedInProfilePatch(): boolean {
  const loginId = getStoredLoginIdentifier()?.trim();
  if (!loginId) {
    return true;
  }
  if (isPhoneLiteRegisterSyntheticEmail(loginId)) {
    return false;
  }
  return loginId.includes('@');
}

function pickPatchFields(
  payload: Record<string, unknown>,
  keys: readonly PrivateClientBookingField[],
): Record<string, unknown> {
  const patch: Record<string, unknown> = {};
  for (const key of keys) {
    const value = payload[key];
    if (value === undefined || value === null || value === '') {
      continue;
    }
    patch[key] = value;
  }
  return patch;
}

export function buildPrivateClientProfileCompletionPatch(
  payload: Record<string, unknown>,
  onlyFields?: readonly PrivateClientBookingField[],
): Record<string, unknown> {
  const keys = onlyFields ?? PROFILE_COMPLETION_PATCH_KEYS;
  return sanitizeProfilePatchBody({
    ...pickPatchFields(payload, keys),
    ...pickGeoFields(payload),
  });
}

export function buildSignedInProfileCompletionPatch(
  payload: Record<string, unknown>,
  onlyFields?: readonly PrivateClientBookingField[],
): Record<string, unknown> {
  const patch = buildPrivateClientProfileCompletionPatch(payload, onlyFields);
  if (
    shouldOmitEmailFromSignedInProfilePatch() ||
    (typeof patch.email === 'string' && isPhoneLiteRegisterSyntheticEmail(patch.email))
  ) {
    delete patch.email;
  }
  return patch;
}

export function isProfilePatchIdentityConflictError(err: unknown): boolean {
  if (!(err instanceof AssetRequestError)) {
    return false;
  }
  if (err.httpStatus !== 409) {
    return false;
  }
  const text = `${err.message} ${err.rawBody ?? ''}`.toLowerCase();
  return text.includes('already exists') || text.includes('conflict');
}

export type ProfileCompletionPatchAttempt = {
  body: Record<string, unknown>;
  label: string;
};

/** True when PATCH /profile rejected unknown geo fields (retry without country/state). */
export function isProfilePatchUnknownGeoError(err: unknown): boolean {
  if (!(err instanceof AssetRequestError) || err.httpStatus !== 400) {
    return false;
  }
  const text = `${err.message} ${err.rawBody ?? ''}`.toLowerCase();
  return (
    text.includes('country_id') ||
    text.includes('state_id') ||
    text.includes('whitelist') ||
    text.includes('should not exist')
  );
}

/** Try names first; never treat occupation/DOB-only as a successful registration. */
export function signedInProfileCompletionPatchAttempts(
  payload: Record<string, unknown>,
): ProfileCompletionPatchAttempt[] {
  const attempts: ProfileCompletionPatchAttempt[] = [
    {
      label: 'profile',
      body: buildSignedInProfileCompletionPatch(payload),
    },
    {
      label: 'profile-names-and-details',
      body: buildSignedInProfileCompletionPatch(payload, [
        'first_name',
        'last_name',
        'occupation',
        'birth_date',
      ]),
    },
    {
      label: 'profile-names-and-dob',
      body: buildSignedInProfileCompletionPatch(payload, ['first_name', 'last_name', 'birth_date']),
    },
  ];
  return attempts.filter((attempt) => Object.keys(attempt.body).length > 0);
}
